<template>
	<div>
		<page-title :name="pageName" :title="pageTitle" :breadcrumbs="pageBreadcrumbs" />

		<div v-if="requestTypes" class="filters-container">
			<div class="container">
				<ValidationObserver ref="form" v-slot="{ pristine }">
					<form autocomplete="off" @submit.prevent="sendForm" @keyup.enter="sendForm" @reset.prevent="resetForm">
						<div class="filters-list">
							<div class="row">
								<div class="col-md-6 col-sm-6 col-xs-12">
									<form-input v-model="filterTitle" label="Misc.FilterByKeyword" name="filter_title"></form-input>
								</div>
								<div class="col-md-6 col-sm-6 col-xs-12">
									<request-type-selector
										:show-label="true"
										:selected="filterRequestTypeId"
										label="Misc.FilterByMatter"
									></request-type-selector>
								</div>
								<div class="col-md-12 col-sm-12 col-xs-12 text-center margin-top-20">
									<form-button label="Button.Reset" component-class="button-3" type="reset"></form-button>
									<form-button
										:disabled="pristine || loading"
										:is-loading="loading"
										label="Button.Filter"
										component-class="button-6"
									></form-button>
								</div>
							</div>
						</div>
					</form>
				</ValidationObserver>
			</div>
		</div>

		<div class="section-types padding-top-20 padding-bottom-100">
			<div class="container">
				<div class="row">
					<stories-container-box v-for="(value, key) in stories" :key="key" :story="value"></stories-container-box>
					<div class="list col-md-12">
						<div v-if="!haveStories && !noData" class="list-loading">
							<loading-animation v-once />
						</div>
						<div v-else-if="!haveStories && noData" class="list-no-results">
							{{ $t('Misc.NoResults') }}
						</div>
						<div v-else-if="pageHasMore" class="list-load-more">
							<a href="#" @click.prevent="loadMore">{{ $t('Misc.LoadMore') }}</a>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import AccountInfo from '@/mixins/AccountInfo'
import FormButton from '@/components/atoms/FormButton'
import FormInput from '@/components/atoms/FormInput'
import LoadingAnimation from '@/utils/commons/LoadingAnimation'
import PageInfo from '@/mixins/PageInfo'
import PageTitle from '@/components/partials/PageTitle'
import RequestTypeSelector from '@/utils/commons/RequestTypeSelector'
import ServiceHelper from '@/mixins/ServiceHelper'
import StoriesContainerBox from '@/components/sections/boxes/StoriesContainerBox'

export default {
	name: 'StoriesList',
	components: {
		FormButton,
		FormInput,
		LoadingAnimation,
		PageTitle,
		RequestTypeSelector,
		StoriesContainerBox,
	},
	mixins: [AccountInfo, PageInfo, ServiceHelper],
	data() {
		return {
			filterChange: false,
			filterEnable: false,
			filterRequestTypeId: null,
			filterTitle: null,
			loading: false,
			noData: false,
			pageToNotIndexFromRobots: true,
			requestTypes: null,
			stories: [],
		}
	},
	computed: {
		haveStories() {
			return this.stories && this.stories.length > 0
		},
	},
	created() {
		this.stories = []

		if (sessionStorage.getItem('ius_request_types')) {
			// If available, use session request types
			this.requestTypes = JSON.parse(sessionStorage.getItem('ius_request_types'))
		} else {
			// Retrieve request types from API and store in session storage
			this.loadRequestTypesData()
		}

		// Retrieve story from API
		this.loadData()
	},
	mounted() {
		// Listen for request type input updated event
		this.$root.$on('request-type-input-updated-event', (result) => {
			this.filterRequestTypeId = result.value
			this.filterChange = true
			this.pageNumber = 1
		})

		// Reset filter request type when page load
		this.$root.$emit('request-type-input-updated-event', {
			value: this.$route.query.filter ? this.$route.query.filter : '',
		})
	},
	destroyed() {
		// This prevents from having memory leaks
		this.$root.$off('request-type-input-updated-event')
	},
	methods: {
		loadData() {
			// Get data
			axios
				.get('/stories/filtered', {
					params: {
						page: this.pageNumber,
						page_size: this.pageSize,
						...(this.$route.query.filter || this.filterRequestTypeId
							? { request_type_id: this.filterRequestTypeId ? this.filterRequestTypeId : this.$route.query.filter }
							: {}),
					},
				})
				.then((response) => {
					if (response.data.info) {
						this.noData = false
						this.stories = this.stories.concat(response.data.info)
						this.pageHasMore = this.hasMore(response.data)
					} else {
						this.stories = []
						this.noData = true
					}
				})
				.catch((error) => {
					console.log(error)
				})
		},
		loadRequestTypesData() {
			// Get data
			axios
				.get('/request-types', {
					params: this.loadAllParams,
				})
				.then((response) => {
					this.requestTypes = response.data.info
					sessionStorage.setItem('ius_request_types', JSON.stringify(this.requestTypes))
				})
				.catch((error) => {
					console.log(error)
				})
		},
		resetForm() {
			this.filterChange = true
			this.filterEnable = false
			this.filterTitle = ''
			this.filterRequestTypeId = ''
			this.stories = []
			this.$router.replace({ query: null })
			this.pageNumber = 1

			// Reset filter request type
			this.$root.$emit('request-type-input-updated-event', {
				value: '',
			})

			this.loadData()
		},
		sendForm() {
			this.$refs.form.validate().then(() => {
				this.loading = true
				this.pageNumber = 1

				// Get data
				axios
					.get('/stories/filtered', {
						params: {
							page: this.pageNumber,
							page_size: this.pageSize,
							...(this.filterRequestTypeId ? { request_type_id: this.filterRequestTypeId } : {}),
							title: this.filterTitle,
						},
					})
					.then((response) => {
						this.loading = false

						if (response.data.info) {
							this.noData = false
							if (this.filterEnable && !this.filterChange) {
								this.stories = this.stories.concat(response.data.info)
								this.filterChange = false
							} else {
								this.stories = response.data.info
								this.filterChange = true
								this.filterEnable = true
							}

							this.pageHasMore = this.hasMore(response.data)
						} else {
							this.stories = []
							this.noData = true
						}
					})
					.catch(() => {
						this.loading = false
						this.stories = []
						this.noData = true
					})
			})
		},
	},
}
</script>



// WEBPACK FOOTER //
// src/views/core/stories/StoriesList.vue